import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'

// ─── Types ───────────────────────────────────────────────────

export interface NoizaGmv {
  total_gmv: number
  total_orders: number
  avg_ticket: number
}

export interface NoizaGmvDaily {
  day: string
  gmv: number
  orders: number
}

export interface NoizaGmvByRestaurant {
  restaurant_id: string
  restaurant_name: string
  slug: string
  gmv: number
  orders: number
  avg_ticket: number
}

export interface NoizaChannelDistribution {
  channel: string
  orders: number
  pct: number
}

export interface NoizaSelfServicePct {
  self_service_pct: number
  waiter_pct: number
}

export interface NoizaActiveRestaurants {
  active_count: number
}

export interface NoizaUniqueCustomers {
  unique_count: number
}

export interface NoizaRepeatCustomers {
  repeat_count: number
  repeat_pct: number
}

export interface NoizaMetricsData {
  gmv: NoizaGmv
  gmvDaily: NoizaGmvDaily[]
  gmvByRestaurant: NoizaGmvByRestaurant[]
  channelDistribution: NoizaChannelDistribution[]
  selfServicePct: NoizaSelfServicePct
  activeRestaurants: number
  uniqueCustomers: number
  repeatCustomers: NoizaRepeatCustomers
}

const DEFAULT_DATA: NoizaMetricsData = {
  gmv: { total_gmv: 0, total_orders: 0, avg_ticket: 0 },
  gmvDaily: [],
  gmvByRestaurant: [],
  channelDistribution: [],
  selfServicePct: { self_service_pct: 0, waiter_pct: 0 },
  activeRestaurants: 0,
  uniqueCustomers: 0,
  repeatCustomers: { repeat_count: 0, repeat_pct: 0 },
}

// ─── Main Hook ─────────────────────────────────────────────

export function useNoizaMetrics() {
  const [data, setData] = useState<NoizaMetricsData>(DEFAULT_DATA)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const [
        gmvRes,
        gmvDailyRes,
        gmvByRestRes,
        channelRes,
        selfServiceRes,
        activeRes,
        uniqueRes,
        repeatRes,
      ] = await Promise.all([
        supabase.from('v_noiza_gmv').select('*').single(),
        supabase
          .from('v_noiza_gmv_daily')
          .select('*')
          .order('day', { ascending: true }),
        supabase
          .from('v_noiza_gmv_by_restaurant')
          .select('*')
          .order('gmv', { ascending: false })
          .limit(20),
        supabase.from('v_noiza_channel_distribution').select('*'),
        supabase.from('v_noiza_self_service_pct').select('*').single(),
        supabase.from('v_noiza_active_restaurants').select('*').single(),
        supabase.from('v_noiza_unique_customers').select('*').single(),
        supabase.from('v_noiza_repeat_customers').select('*').single(),
      ])

      setData({
        gmv: gmvRes.data || DEFAULT_DATA.gmv,
        gmvDaily: (gmvDailyRes.data || []).map((row: any) => ({
          day: row.day || row.date || '',
          gmv: row.gmv || row.total_gmv || row.revenue || 0,
          orders: row.orders || row.total_orders || row.order_count || 0,
        })),
        gmvByRestaurant: (gmvByRestRes.data || []).map((row: any) => ({
          restaurant_id: row.restaurant_id || '',
          restaurant_name: row.restaurant_name || row.name || 'Sin nombre',
          slug: row.slug || '',
          gmv: row.gmv || row.total_gmv || row.revenue || 0,
          orders: row.orders || row.total_orders || row.order_count || 0,
          avg_ticket: row.avg_ticket || row.average_ticket || 0,
        })),
        channelDistribution: (channelRes.data || []).map((row: any) => ({
          channel: row.channel || row.order_channel || row.source || 'other',
          orders: row.orders || row.order_count || row.total || 0,
          pct: row.pct || row.percentage || 0,
        })),
        selfServicePct: selfServiceRes.data || DEFAULT_DATA.selfServicePct,
        activeRestaurants:
          activeRes.data?.active_count ||
          activeRes.data?.count ||
          activeRes.data?.active_restaurants ||
          0,
        uniqueCustomers:
          uniqueRes.data?.unique_count ||
          uniqueRes.data?.count ||
          uniqueRes.data?.unique_customers ||
          0,
        repeatCustomers: {
          repeat_count:
            repeatRes.data?.repeat_count ||
            repeatRes.data?.count ||
            repeatRes.data?.repeat_customers ||
            0,
          repeat_pct:
            repeatRes.data?.repeat_pct ||
            repeatRes.data?.pct ||
            repeatRes.data?.percentage ||
            0,
        },
      })
    } catch (err: any) {
      console.error('[NOIZA Platform] Error fetching metrics:', err)
      setError(err?.message || 'Error loading platform metrics')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAll()
    const interval = setInterval(fetchAll, 60000) // refresh every minute
    return () => clearInterval(interval)
  }, [fetchAll])

  return { data, loading, error, refetch: fetchAll }
}
